/**
 * "Tests that need your readings" (trust audit Phase 1; Corbin 10/22; mockup
 * AsHAGZ2vsJr3FFhVuW4Mib, C). The office recorded a signed paper as a FAIL;
 * the TECH enters the numbers, never the office. One card at the top of his
 * home screen, only when there is one. No bell, no text.
 */
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, attachmentDownloadUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HowItWorks } from "@/components/how-it-works";
import { ProofChip } from "@/components/proof/proof-chip";
import { PROOF_CHIPS_DEFAULT_ON, PROOF_CHIPS_PREF_KEY, type Proof } from "@/lib/proof";
import { useUserPreference } from "@/lib/user-preference";

export interface PaperReadingsTest {
    jobId: string;
    assetId: string;
    testedAt: string | null;
    scanAttachmentId: string | null;
    jobNumber: string | null;
    customerName: string | null;
    deviceLabel: string | null;
    serial: string | null;
    /** Proof chips S3: "? Missing: your readings off the paper". Drawn unless the person turned the How-sure chips off. */
    proof?: Proof | null;
}

export function paperReadingsHref(t: Pick<PaperReadingsTest, "jobId" | "assetId">): string {
    return `/field/backflow/${t.jobId}?step=paper&asset=${t.assetId}`;
}

function shortDate(iso: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? ""
        : d.toLocaleDateString("en-US", { month: "numeric", day: "numeric", timeZone: "America/Chicago" });
}

export function PaperReadingsCardView({
    tests,
    onOpen,
    showProof = false,
}: {
    tests: PaperReadingsTest[];
    onOpen: (t: PaperReadingsTest) => void;
    /** The iPad's only proof chip (Corbin 10/28): on this card, on unless the person turned the How-sure chips off. */
    showProof?: boolean;
}) {
    if (tests.length === 0) return null;
    const first = tests[0];
    return (
        <div
            className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-600/50 dark:bg-amber-950/30 dark:text-amber-100"
            data-testid="paper-readings-card"
        >
            <div className="flex items-start justify-between gap-2">
                <p className="font-semibold">
                    {tests.length === 1
                        ? "1 failed test needs your test readings"
                        : `${tests.length} failed tests need your test readings`}
                </p>
                <HowItWorks
                    title="How failed tests from paper work"
                    testId="how-readings-card"
                    steps={[
                        {
                            title: "The office has your signed test paper",
                            text: "It says the device failed. The office scans the paper, but you enter the readings.",
                            picture: "tech-readings-card.png",
                            alt: "The card on Today with the button ringed",
                        },
                        {
                            title: "Tap the button and enter the readings from the paper",
                            text: "The test screen opens with the scan on top. Save when you're done; the card goes away.",
                            picture: "tech-open-scan.png",
                            alt: "The paper banner with Open the scan ringed",
                        },
                    ]}
                />
            </div>
            <p className="mt-0.5">
                {[first.customerName, first.deviceLabel].filter(Boolean).join(" · ")}
                {first.testedAt ? ` · Failed ${shortDate(first.testedAt)}` : ""}
            </p>
            <p className="mt-0.5 text-xs text-amber-900/80 dark:text-amber-100/80">
                The office has your signed test paper. Enter the readings from it.
            </p>
            {showProof && first.proof && (
                <div className="mt-1.5">
                    <ProofChip proof={first.proof} place="busy" testId="paper-readings-proof" />
                </div>
            )}
            <Button size="sm" className="mt-2" onClick={() => onOpen(first)}>
                Enter the test readings from the paper
            </Button>
        </div>
    );
}

/**
 * The How-sure switch, read the quiet way, for anyone on the field app (techs
 * included). ON unless this person turned the chips off for themselves (same
 * key and default as `useProofChipsSwitch`); never shows a save toast here.
 */
export function useHowSureOnHere(): boolean {
    const [value, , { loaded }] = useUserPreference<boolean>(PROOF_CHIPS_PREF_KEY, PROOF_CHIPS_DEFAULT_ON);
    return loaded && value !== false;
}

export function PaperReadingsCard() {
    const navigate = useNavigate();
    const showProof = useHowSureOnHere();
    const q = useQuery({
        queryKey: ["paper-readings", "mine"],
        queryFn: () => api.get<{ tests: PaperReadingsTest[] }>("/api/backflow/paper-readings/mine"),
        staleTime: 60_000,
    });
    return (
        <PaperReadingsCardView
            tests={q.data?.tests ?? []}
            onOpen={(t) => navigate(paperReadingsHref(t))}
            showProof={showProof}
        />
    );
}

/**
 * On the test screen: "Readings from the paper", with the signed scan one tap
 * away, so he types exactly what it says. Shown only for a device the office
 * sent him.
 */
export function PaperScanBanner({ jobId, assetId }: { jobId: string | undefined; assetId: string }) {
    const q = useQuery({
        queryKey: ["paper-readings", "mine"],
        queryFn: () => api.get<{ tests: PaperReadingsTest[] }>("/api/backflow/paper-readings/mine"),
        staleTime: 60_000,
    });
    const t = (q.data?.tests ?? []).find((x) => x.jobId === jobId && x.assetId === assetId);
    if (!t) return null;
    return (
        <div
            className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-600/50 dark:bg-amber-950/30 dark:text-amber-100"
            data-testid="paper-scan-banner"
        >
            <div className="flex items-center justify-between gap-2">
                <p className="font-semibold">Test readings from your signed paper</p>
                <HowItWorks
                    title="How entering readings from paper works"
                    testId="how-paper-readings"
                    steps={[
                        {
                            title: "Open the scan",
                            text: "It's the test paper you signed. Keep it open next to the app.",
                            picture: "tech-open-scan.png",
                            alt: "The paper banner with Open the scan ringed",
                        },
                        {
                            title: "Enter each reading exactly as the paper shows",
                            text: "Check valves, relief, PSID. Then save it like any test. It goes in as a FAIL with your readings.",
                        },
                        {
                            title: "If your numbers pass",
                            text: "The app stops you, because the paper says it failed. Call the office.",
                        },
                    ]}
                />
            </div>
            <p className="mt-0.5 text-xs">
                The paper says this device failed. Type exactly what it shows, then save. If your numbers pass, the app
                stops you.
            </p>
            {t.scanAttachmentId && (
                <a
                    className="mt-2 inline-block text-xs font-semibold underline"
                    href={attachmentDownloadUrl(t.scanAttachmentId, { disposition: "inline" })}
                    target="_blank"
                    rel="noreferrer"
                >
                    Open the scan
                </a>
            )}
        </div>
    );
}
